import { ContentSkeleton } from '@/components/loading/Skeleton'
import { cn } from '@/lib/utils'
import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../util/api';
import { StreamMetric } from '../types/infrastructure';
import { useStreamMetricsWebSocket, useStreamStatusWebSocket, useStreamsWebSocket } from '../hooks/useWebSocket';
import { Badge, Button, Card, EmptyState, IconButton, Meter, Notice, toast, type BadgeTone } from '@/components/play';
import { Signal, SignalHigh, SignalZero, TriangleAlert, X } from 'lucide-react';

interface Stream {
  id: string;
  name: string;
  status: string;
  pod_name?: string;
  started_at?: string;
  fps?: number;
  bitrate?: number;
  latency_ms?: number;
}

export default function StreamsMonitorPage() {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [selectedStream, setSelectedStream] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Map<string, StreamMetric[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time metrics for selected stream
  useStreamMetricsWebSocket(
    selectedStream || '',
    (newMetrics) => {
      if (selectedStream) {
        setMetrics(prev => {
          const updated = new Map(prev);
          const existing = updated.get(selectedStream) || [];
          updated.set(selectedStream, [...existing, newMetrics].slice(-100)); // Keep last 100 metrics
          return updated;
        });
      }
    }
  );

  useEffect(() => {
    // Initial load only; rely on WebSockets and explicit actions afterward
    loadStreams();
  }, []);

  useEffect(() => {
    if (selectedStream) {
      loadStreamMetrics(selectedStream);
    }
  }, [selectedStream]);

  const loadStreams = async () => {
    try {
      const resp: any = await api.getStreams();
      const items = (resp?.data || []).map((item: any) => ({
        id: item.id,
        name: item.attributes?.name,
        status: item.attributes?.status,
        pod_name: item.attributes?.pod_name, // may be undefined if not serialized
        started_at: item.attributes?.started_at,
      } as Stream));
      setStreams(items);
      setError(null);
    } catch (err) {
      setError('We couldn’t load streams.');
      console.error('Streams load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStreamMetrics = async (streamId: string) => {
    try {
      const data: any = await api.getStreamMetrics(streamId, { limit: 100 });
      const metricsArray = Array.isArray(data) ? data : data.data || [];
      setMetrics(prev => new Map(prev).set(streamId, metricsArray));
    } catch (err) {
      console.error('Failed to load metrics for stream:', streamId, err);
    }
  };

  const handleStartStream = async (streamId: string) => {
    try {
      await api.startStream(streamId);
      // Optimistically mark provisioning
      setStreams(prev => prev.map(s => s.id === streamId ? { ...s, status: 'provisioning' } : s));
    } catch (err) {
      console.error('Failed to start stream:', err);
      toast({ title: 'Failed to start stream', tone: 'error' });
    }
  };

  // Real-time stream status updates for selected stream
  useStreamStatusWebSocket(selectedStream || '', (msg) => {
    if (!selectedStream) return;
    if (msg?.type === 'status_update' && msg.stream?.data) {
      const attrs = msg.stream.data.attributes;
      setStreams(prev => prev.map(s => s.id === msg.stream.data.id ? { ...s, status: attrs.status } : s));
    }
    if (msg?.type === 'error') {
      setStreams(prev => prev.map(s => s.id === selectedStream ? { ...s, status: 'error' } : s));
    }
  });

  // List-level updates for any stream changes
  useStreamsWebSocket(
    (streamPayload) => {
      const id = streamPayload?.data?.id
      const attrs = streamPayload?.data?.attributes || {}
      if (!id) return
      setStreams(prev => {
        const exists = prev.some(s => s.id === id)
        if (exists) {
          return prev.map(s => s.id === id ? { ...s, name: attrs.name ?? s.name, status: attrs.status ?? s.status, pod_name: attrs.pod_name ?? s.pod_name, started_at: attrs.started_at ?? s.started_at } : s)
        } else {
          // Add new stream minimally
          const item = { id, name: attrs.name, status: attrs.status, pod_name: attrs.pod_name, started_at: attrs.started_at } as Stream
          const next = [item, ...prev]
          toast({ title: `New stream added: ${attrs.name || id}`, tone: 'success', timeout: 3000 })
          return next
        }
      })
    },
    (streamId) => {
      setStreams(prev => prev.filter(s => s.id !== streamId))
      if (selectedStream === streamId) setSelectedStream(null)
    }
  )

  // Provisioning notice: show warning if selected stream remains provisioning > 30s
  const [provisioningSince, setProvisioningSince] = useState<number | null>(null);
  const selected = streams.find(s => s.id === selectedStream || '')

  useEffect(() => {
    if (!selectedStream || !selected) {
      setProvisioningSince(null);
      return;
    }
    if (selected.status === 'provisioning') {
      if (!provisioningSince) setProvisioningSince(Date.now());
    } else {
      setProvisioningSince(null);
    }
  }, [selectedStream, selected?.status]);

  const activeCount = useMemo(() => streams.filter(s => s.status === 'active').length, [streams]);
  const totalCount = streams.length;

  const heading = <h1 className="text-3xl font-semibold text-neutral-10">Streams</h1>;

  if (loading) {
    return (
      <div className="app-page max-w-7xl space-y-8">
        {heading}
        <ContentSkeleton label="Loading streams" variant="list" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-page max-w-7xl space-y-8">
        {heading}
        <Notice
          tone="error"
          action={(
            <Button
              variant="secondary"
              size="compact"
              onClick={() => {
                setLoading(true);
                void loadStreams();
              }}
            >
              Try again
            </Button>
          )}
        >
          {error}
        </Notice>
      </div>
    );
  }

  return (
    <div className="app-page max-w-7xl">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        {heading}
        <div className="flex items-center gap-3">
          <Badge tone="neutral">{activeCount} active</Badge>
          <Badge tone="neutral">{totalCount} total</Badge>
        </div>
      </div>

      {selected && provisioningSince && Date.now() - provisioningSince > 30000 && (
        <Notice tone="warning" className="mb-6">
          Provisioning is taking longer than expected. Check the worker logs and the pod health for stream {selected.id}.
        </Notice>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {streams.map(stream => (
          <StreamCard
            key={stream.id}
            stream={stream}
            isSelected={selectedStream === stream.id}
            onToggle={() => setSelectedStream(current => current === stream.id ? null : stream.id)}
            onStart={() => handleStartStream(stream.id)}
          />
        ))}
        {streams.length === 0 && (
          <EmptyState
            className="col-span-full"
            icon={<Signal />}
            title="No streams yet"
            description="Streams appear here once cameras are added."
          />
        )}
      </div>

      {/* Non-modal, so the cards stay usable; it fits a 320px screen. */}
      {selectedStream && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(24rem,calc(100vw-2rem))] rounded-2xl shadow-theme-m">
          <MetricsPanel
            streamId={selectedStream}
            metrics={metrics.get(selectedStream) || []}
            stream={streams.find(s => s.id === selectedStream)}
            onClose={() => setSelectedStream(null)}
          />
        </div>
      )}
    </div>
  );
}

/** Active is the stream's lifecycle, not proof that video is arriving, so it takes the accent tone, as on the Admin
 *  health dashboard: success green is kept for live media evidence. */
function streamStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'active':
    case 'started':
      return 'accent';
    case 'provisioning':
    case 'starting':
      return 'neutral';
    case 'failed':
    case 'error':
      return 'error';
    default:
      return 'quiet';
  }
}

function StreamCard({
  stream,
  isSelected,
  onToggle,
  onStart,
}: {
  stream: Stream;
  isSelected: boolean;
  onToggle: () => void;
  onStart: () => void;
}) {
  const isActive = stream.status === 'active';

  return (
    <Card
      // The whole card toggles this stream's metrics panel; Start stays its own button above it.
      selected={isSelected}
      selectionTone="media"
      action={{ label: `${stream.name || stream.id} metrics`, onClick: onToggle }}
      title={<span className="truncate">{stream.name}</span>}
      icon={isActive ? <SignalHigh /> : <SignalZero />}
      description={stream.pod_name && <span className="block truncate font-data text-xs">{stream.pod_name}</span>}
      actions={<Badge tone={streamStatusTone(stream.status)} className="capitalize">{stream.status}</Badge>}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-neutral-7">
          {stream.started_at ? (
            <>Started {new Date(stream.started_at).toLocaleTimeString()}</>
          ) : (
            <>Not started</>
          )}
        </div>
        {!isActive && (
          <Button variant="secondary" size="compact" onClick={onStart}>
            Start
          </Button>
        )}
      </div>
    </Card>
  );
}

function MetricsPanel({
  streamId,
  metrics,
  stream,
  onClose
}: {
  streamId: string;
  metrics: StreamMetric[];
  stream?: Stream;
  onClose?: () => void;
}) {
  const latestMetric = metrics[metrics.length - 1];
  
  return (
    <Card
      title="Stream metrics"
      description={stream?.name}
      actions={onClose && (
        <IconButton label="Close" variant="ghost" size="compact" onClick={onClose}>
          <X />
        </IconButton>
      )}
    >
      <div className="space-y-4">
        {latestMetric ? (
          <>
            <MetricItem
              label="FPS"
              value={latestMetric.fps}
              unit=""
              alertLevel={latestMetric.alert_level}
            />
            <MetricItem
              label="Bitrate"
              value={latestMetric.bitrate}
              unit="kbps"
              alertLevel={latestMetric.alert_level}
            />
            <MetricItem
              label="Latency"
              value={latestMetric.latency_ms}
              unit="ms"
              alertLevel={latestMetric.alert_level}
            />
            <MetricItem
              label="Dropped frames"
              value={latestMetric.dropped_frames}
              unit=""
              alertLevel={latestMetric.dropped_frames > 100 ? 'warning' : 'healthy'}
            />
            <MetricItem
              label="Reconnects"
              value={latestMetric.reconnect_count}
              unit=""
              alertLevel={latestMetric.reconnect_count > 5 ? 'critical' : 'healthy'}
            />
            {latestMetric.quality_score !== undefined && (
              <Meter
                className="pt-4"
                label="Quality score"
                value={latestMetric.quality_score}
                levels={{ degraded: 80, poor: 60 }}
                format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
              />
            )}
            <div className="text-xs text-neutral-7 text-center pt-2">
              Updated {new Date(latestMetric.recorded_at).toLocaleTimeString()}
            </div>
          </>
        ) : (
          <div className="text-center text-neutral-7">
            No metrics yet
          </div>
        )}
      </div>
    </Card>
  );
}

function MetricItem({
  label,
  value,
  unit,
  alertLevel
}: {
  label: string;
  value: number | null;
  unit: string;
  alertLevel?: string;
}) {
  // A glyph and words carry the alert as well as the colour.
  const alert = alertLevel === 'warning' || alertLevel === 'critical' ? alertLevel : null;

  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-neutral-7">{label}</span>
      <span
        className={cn(
          'inline-flex items-center gap-1 text-lg font-semibold font-data',
          alert === 'warning' ? 'text-warning' : alert === 'critical' ? 'text-error' : 'text-neutral-10',
        )}
      >
        {alert && <TriangleAlert size={16} aria-hidden="true" />}
        {value != null ? `${value}${unit && ` ${unit}`}` : '—'}
        {alert && <span className="sr-only">, {alert}</span>}
      </span>
    </div>
  );
}
